/**
 * DayOfWeekChartV2 Component
 *
 * Displays SPLH breakdown by day of week as a column chart.
 * Shows current SPLH with comparison data in tooltip.
 *
 * Features:
 * - Bar chart showing current SPLH by weekday (Mon-Sun)
 * - Tooltip shows comparison SPLH and delta percentage
 * - Purple color scheme consistent with dashboard theme
 *
 * @module pages/common/dashboards/SPLHDashboard/components/BreakdownSectionV2/components/DayOfWeekChartV2
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, Typography, Skeleton } from '@mui/material';
import { formatSPLH } from '../../../utils/formatUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Day order starting from Monday
const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * DayOfWeekChartV2 Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of day breakdown objects from V2 API
 * @param {boolean} [props.loading=false] - Whether data is loading
 * @param {string} [props.height='300px'] - Chart height
 * @returns {React.Component} DayOfWeekChartV2 component
 */
const DayOfWeekChartV2 = ({ data = [], loading = false, height = '300px' }) => {
  // Sort and organize data by day of week (Monday first)
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Create a map of data by day label
    const dataByDay = new Map();
    data.forEach((item) => {
      dataByDay.set(item.label, item);
    });

    // Build ordered arrays for all 7 days
    const currentValues = [];
    const rawData = [];

    DAY_ORDER.forEach((day) => {
      const dayData = dataByDay.get(day);
      if (dayData) {
        currentValues.push(dayData.currentSPLH || 0);
        rawData.push(dayData);
      } else {
        // Fill with 0 if day is missing
        currentValues.push(0);
        rawData.push({ label: day, currentSPLH: 0, comparisonSPLH: 0, deltaPct: 0 });
      }
    });

    return {
      labels: DAY_LABELS,
      datasets: [
        {
          label: 'Current SPLH',
          data: currentValues,
          backgroundColor: '#7C3AED',
          borderColor: '#6D28D9',
          borderWidth: 1,
          borderRadius: 4,
          hoverBackgroundColor: '#6D28D9',
        },
      ],
      rawData,
    };
  }, [data]);

  // Calculate y-axis range with padding to ensure bars sit in middle band (25-75% of chart height)
  // Algorithm ensures bars never hug top/bottom and differences are easy to see
  const yAxisRange = useMemo(() => {
    // Constants
    const MIN_RANGE = 0.20;           // Minimum effective range to prevent flat charts
    const BOTTOM_PADDING_FACTOR = 0.25;
    const TOP_PADDING_FACTOR = 0.25;
    const MIN_ABSOLUTE_PADDING = 0.10;
    const TICK_STEP = 0.1;            // Round to nearest 0.1 for nice labels

    if (!data || data.length === 0) {
      return { min: 0, max: 100 };
    }

    const values = data
      .map((d) => d.currentSPLH)
      .filter((v) => v != null && v > 0);

    if (values.length === 0) {
      return { min: 0, max: 100 };
    }

    // Step 1: Get raw data range
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const rawRange = dataMax - dataMin;

    // Step 2: Protect against tiny or flat ranges
    const range = rawRange < MIN_RANGE ? MIN_RANGE : rawRange;

    // Step 3: Compute padding for top and bottom
    const bottomPadding = Math.max(range * BOTTOM_PADDING_FACTOR, MIN_ABSOLUTE_PADDING);
    const topPadding = Math.max(range * TOP_PADDING_FACTOR, MIN_ABSOLUTE_PADDING);

    // Step 4: Compute Y-axis limits
    let yMin = dataMin - bottomPadding;
    let yMax = dataMax + topPadding;

    // Step 5: Clamp to zero (SPLH can't be negative)
    if (yMin < 0) {
      yMin = 0;
    }

    // Step 6: Round to nice tick values
    yMin = Math.floor(yMin / TICK_STEP) * TICK_STEP;
    yMax = Math.ceil(yMax / TICK_STEP) * TICK_STEP;

    return { min: yMin, max: yMax };
  }, [data]);

  // Chart options
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'end',
        align: 'start',
        offset: 4,
        color: '#ffffff',
        font: {
          size: 11,
          weight: 600,
          family: 'Roboto, sans-serif',
        },
        formatter: (value) => {
          if (value === 0 || value == null) return '';
          return `$${value.toFixed(2)}`;
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1a1a1a',
        bodyColor: '#666',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const index = tooltipItems[0].dataIndex;
              return DAY_ORDER[index];
            }
            return '';
          },
          label: (context) => {
            const index = context.dataIndex;
            const item = chartData?.rawData[index];
            if (!item) return [];

            const currentSPLH = formatSPLH(item.currentSPLH);
            const comparisonSPLH = formatSPLH(item.comparisonSPLH);
            const deltaPct = item.deltaPct;
            const sign = deltaPct >= 0 ? '+' : '';

            return [
              `Current: ${currentSPLH}`,
              `Comparison: ${comparisonSPLH}`,
              `Change: ${sign}${deltaPct?.toFixed(1)}%`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#9ca2b7',
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
            weight: 500,
          },
        },
      },
      y: {
        min: yAxisRange.min,
        max: yAxisRange.max,
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
        },
        ticks: {
          color: '#9ca2b7',
          font: {
            size: 11,
            family: 'Roboto, sans-serif',
          },
          callback: (value) => `$${value.toFixed(2)}`,
          padding: 8,
        },
        title: {
          display: true,
          text: 'SPLH',
          color: '#9ca2b7',
          font: {
            size: 12,
            family: 'Roboto, sans-serif',
          },
        },
      },
    },
  }), [chartData, yAxisRange]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ height, p: 2 }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Empty state
  if (!chartData) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fafafa',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No day of week data available
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Data will appear when available for the selected date range
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height, position: 'relative' }}>
      <Bar data={chartData} options={options} />
    </Box>
  );
};

DayOfWeekChartV2.propTypes = {
  /** Array of day breakdown objects from V2 API */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      currentSPLH: PropTypes.number,
      comparisonSPLH: PropTypes.number,
      deltaPct: PropTypes.number,
      currentSales: PropTypes.number,
      currentHours: PropTypes.number,
    })
  ),
  /** Whether data is loading */
  loading: PropTypes.bool,
  /** Chart height */
  height: PropTypes.string,
};

export default DayOfWeekChartV2;
